import pako from "pako";
import { getProcessedThermalImages } from "../services/getProcessedThermalImages.gql.ts";
import {
  IRImageType,
  ProcessedThermalImages,
} from "../types/getProcessedThermalImages.ts";
import WorkerFactory from "~/workers/workerFactory.ts";
import ThermalImageWorker, {
  ThermalImageWorkerTypes,
} from "../workers/thermalImageWorker.ts";
import { reject } from "lodash";
import { GET_PROCESSED_THERMAL_IMAGES } from "~/constants/swrKey.ts";
import { useCallback, useEffect, useState } from "react";
import { notification } from "antd";
import { ThermalHotspot } from "~/pages/copilotMessage/types/copilot.ts";

export interface CurrentProcessedThermalImageDataModel {
  east: ProcessedThermalImages;
  west: ProcessedThermalImages;
  north: ProcessedThermalImages;
}

const fetcher = async (
  campaignHeatId: string,
  setId: string,
  allHotspots?: ThermalHotspot[],
) => {
  if (!campaignHeatId || !setId) {
    return;
  }
  const workerInstance = WorkerFactory(ThermalImageWorker);

  try {
    const zippedImagesResult = await getProcessedThermalImages({
      campaignHeatId,
      setId,
    });

    const zipArray = zippedImagesResult?.selectedImagesZipped;

    return new Promise<CurrentProcessedThermalImageDataModel>((resolve) => {
      if (zipArray) {
        const pakoOutput = pako.inflate(zipArray, { to: "string" });

        workerInstance.postMessage({
          type: ThermalImageWorkerTypes.GET_PROCESSED_THERMAL_IMAGE_DATA,
          payload: {
            zippedImagesResult,
            pakoOutput,
            IRImageType,
            allHotspots,
          },
        });

        workerInstance.onmessage = (
          e: MessageEvent<{
            type: ThermalImageWorkerTypes;
            payload: CurrentProcessedThermalImageDataModel;
          }>,
        ) => {
          const { type, payload } = e.data;

          if (
            type === ThermalImageWorkerTypes.GET_PROCESSED_THERMAL_IMAGE_DATA
          ) {
            resolve(payload);
            workerInstance.terminate();
          }
        };
      } else {
        reject(undefined);
        workerInstance.terminate();
      }
    });
  } catch (_) {
    workerInstance.terminate();
  }
};

export const useGetProcessedThermalImages = (
  campaignHeatId: string,
  setId: string,
  allHotspots?: ThermalHotspot[],
) => {
  const swrKey = createSWRKey(campaignHeatId, setId);

  const [{ data, loading }, setData] = useState<{
    data?: CurrentProcessedThermalImageDataModel;
    loading: boolean;
  }>({
    data: undefined,
    loading: true,
  });

  const runFetcher = useCallback(async () => {
    try {
      const data = await fetcher(campaignHeatId, setId, allHotspots);
      setData({ data, loading: false });
    } catch (e) {
      notification.error({
        message: "Error",
        description: "Failed to load conversations",
      });

      setData({ loading: false });
    }
  }, [campaignHeatId, setId, allHotspots]);

  useEffect(() => {
    runFetcher();
  }, [swrKey, runFetcher]);

  return {
    processedThermalImages: data,
    processedThermalImagesLoading: loading || data === undefined,
  };
};

const createSWRKey = (campaignHeatId: string, setId: string) => {
  return [GET_PROCESSED_THERMAL_IMAGES, campaignHeatId, setId].join("|");
};
